import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { Button, Title, useToast } from '@zenterprise-inc/ui';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay';

import { cancelApplyMutation as CancelApplyMutationType } from '@/__generated__/cancelApplyMutation.graphql';
import { cancelReasonsV2Query as CancelReasonsV2QueryType } from '@/__generated__/cancelReasonsV2Query.graphql';
import { cancelTritxApplyCancelMutation as TritxApplyCancelMutationType } from '@/__generated__/cancelTritxApplyCancelMutation.graphql';
import { CancelReasonList } from '@/components/tax-refund/cancel/CancelReasonList';
import { checkRefund } from '@/components/tax-refund/common/graphql/query/checkRefund';
import { checkTritxRefund } from '@/components/tax-refund/common/graphql/query/checkTritxRefund';
import { cancelReasonsV2Query, applyCancelMutation, tritxApplyCancelMutation } from '@/graphql/mutation/taxRefund/cancel';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { cancelDataAtom } from '@/lib/stores/cancel-data';

type CancelReasonContentProps = {
  refundId: string;
  name: string;
  resultPath: string;
  businessType: 'indis' | 'corps';
};

export const CancelReasonContent = ({ refundId, name, resultPath, businessType }: CancelReasonContentProps) => {
  const router = useRouter();
  const { sendViewEvent, sendClickEvent } = useUserEventLogger();
  const { showDialog } = useDialog();
  const toast = useToast();
  const environment = useRelayEnvironment();
  const setCancelData = useSetAtom(cancelDataAtom);

  const [selectedValue, setSelectedValue] = useState<null | number>(null);
  const [etcContent, setEtcContent] = useState('');

  const { type } = router.query;
  const taxName = type === 'indis' ? '종합소득세' : type === 'indis-tritx' ? '양도소득세' : type === 'corps' ? '법인세' : '';

  const typeAttribute = useMemo((): Record<string, string> => {
    if (type === 'indis-tritx') {
      return { type: 'capital-gain' };
    }
    if (type === 'indis') {
      return { type: 'global-income' };
    }
    return {};
  }, [type]);

  const itrfCd = type === 'indis-tritx' ? '22' : type === 'corps' ? '31' : '10';

  // 취소 사유 목록 조회
  const data = useLazyLoadQuery<CancelReasonsV2QueryType>(cancelReasonsV2Query, { corpType: businessType.toUpperCase(), itrfCd }, {});
  const reasons = (data?.cancelReasonsV2?.reasons || []).filter((item) => item.active).sort((a, b) => a.sequence - b.sequence);

  useEffect(() => {
    sendViewEvent(`${businessType}-cancel_reason`, typeAttribute);
  }, [businessType, typeAttribute, sendViewEvent]);

  const etcReasonIndex = reasons.findIndex((item) => item.reason.includes('기타'));

  const isTritxRefund = type === 'indis-tritx';
  const [commitApplyCancel, isApplyCancelLoading] = useMutation<CancelApplyMutationType>(applyCancelMutation);
  const [commitTritxApplyCancel, isTritxApplyCancelLoading] = useMutation<TritxApplyCancelMutationType>(tritxApplyCancelMutation);

  const handleChangeSelect = (index: number) => {
    setSelectedValue(index);
  };

  const handleChangeEtcContent = (content: string) => {
    setEtcContent(content);
  };

  const goResultPage = () => {
    setCancelData(undefined);
    sendClickEvent(`${businessType}-cancel_reason_keep-going-dialog-ok`);
    router.replace({ pathname: resultPath, query: { type } });
  };

  const goHomePage = () => {
    sendClickEvent(`${businessType}-cancel_reason_impossible-cancel-ok`, typeAttribute);
    router.push(COMMON_PATHS.환급조회목록);
  };

  const handleContinue = () => {
    sendClickEvent(`${businessType}-cancel_reason_keep-going`, typeAttribute);
    showDialog({
      title: '환급을 계속 진행할게요',
      description: '신속하고 빠짐없이 환급받으실 수 있도록 최선을 다할게요.',
      buttons: {
        primary: {
          text: '확인',
          onClick: goResultPage
        }
      }
    });
  };

  const requestApplyCancel = (success: () => void, fail: () => void) => {
    if (selectedValue === null || isApplyCancelLoading || isTritxApplyCancelLoading) return;

    const commonVariables = {
      refundId,
      reasonId: reasons[selectedValue]?.id ?? '',
      content: etcContent
    };

    const commit = isTritxRefund ? commitTritxApplyCancel : commitApplyCancel;

    commit({
      variables: commonVariables,
      onCompleted: (response) => (response.data.result ? success() : fail()),
      onError: fail
    });
  };

  const handleCancel = async () => {
    if (selectedValue === etcReasonIndex && etcContent.trim() === '') {
      showDialog({
        title: '고민 사유를 입력해주세요.',
        buttons: { primary: { text: '확인' } }
      });
      return;
    }

    sendClickEvent(`${businessType}-cancel_reason_cancel-going`, {
      ...(selectedValue !== null && { reason_id: reasons[selectedValue]?.reason ?? '' }),
      ...typeAttribute
    });

    let tritxRefundStage;
    const refundStage = await checkRefund(environment, refundId);
    if (!isTritxRefund) {
      tritxRefundStage = await checkTritxRefund(environment, refundId);
    }

    if (!isTritxRefund) {
      switch (refundStage?.cancelStage) {
        case 'CancelPossibleBeforeDeclare': {
          sendViewEvent(`${businessType}-cancel_reason_cancel-try-dialog`, { item: 'before-declaration', ...typeAttribute }, { isAllowRetry: false });
          showDialog({
            title: `${taxName} 환급을 취소할까요?`,
            description: '취소가 접수되면 제휴 세무사 검토가 일시중지돼요. 과거 5년치만 환급 가능하니 취소하더라도 환급금 소멸 전에 꼭 받아가세요.',
            buttons: {
              primary: {
                text: '환급 취소',
                variant: 'filled-error',
                onClick: () => {
                  sendClickEvent(`${businessType}-cancel_reason_cancel-try-dialog-next`, typeAttribute);
                  requestApplyCancel(
                    () => {
                      sendViewEvent(
                        `${businessType}-cancel_reason_cancel-done-dialog`,
                        { item: 'before-declaration', ...typeAttribute },
                        { isAllowRetry: true }
                      );
                      showDialog({
                        title: '환급 취소 신청을 접수했어요',
                        description: '환급 진행 상태 확인 후 취소가 완료돼요.',
                        buttons: {
                          primary: {
                            text: '확인',
                            onClick: () => {
                              sendClickEvent(`${businessType}-cancel_reason_cancel-done-dialog-ok`, typeAttribute);
                              goResultPage();
                            }
                          }
                        }
                      });
                    },
                    () => {
                      toast({ content: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.', icon: 'error', iconColor: 'red.600' });
                    }
                  );
                }
              },
              secondary: {
                text: '아니요',
                onClick: () => {
                  sendClickEvent(`${businessType}-cancel_reason_cancel-try-dialog-back`, typeAttribute);
                }
              }
            }
          });
          break;
        }
        case 'CancelPossibleAfterDeclare': {
          sendViewEvent(`${businessType}-cancel_reason_cancel-try-dialog`, { item: 'after-declaration', ...typeAttribute }, { isAllowRetry: false });
          showDialog({
            title: `${taxName} 환급을 취소할까요?`,
            description: '이미 국세청에 신고되어 즉시 취소가 어려워요. 취소를 접수하면 담당자 확인을 거쳐 진행돼요.',
            buttons: {
              primary: {
                text: '환급 취소',
                variant: 'filled-error',
                onClick: () => {
                  sendClickEvent(`${businessType}-cancel_reason_cancel-try-dialog-next`, typeAttribute);
                  requestApplyCancel(
                    () => {
                      sendViewEvent(
                        `${businessType}-cancel_reason_cancel-done-dialog`,
                        { item: 'after-declaration', ...typeAttribute },
                        { isAllowRetry: true }
                      );
                      showDialog({
                        title: '환급 취소 신청을 접수했어요',
                        description: '진행 상태 확인 후 담당자가 연락드릴게요.',
                        buttons: {
                          primary: {
                            text: '확인',
                            onClick: () => {
                              sendClickEvent(`${businessType}-cancel_reason_cancel-done-dialog-ok`, typeAttribute);
                              goResultPage();
                            }
                          }
                        }
                      });
                    },
                    () => {
                      toast({ content: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.', icon: 'error', iconColor: 'red.600' });
                    }
                  );
                }
              },
              secondary: {
                text: '아니요',
                onClick: () => {
                  sendClickEvent(`${businessType}-cancel_reason_cancel-try-dialog-back`, typeAttribute);
                }
              }
            }
          });
          break;
        }
        case 'CancelPossibleImmediately': {
          sendViewEvent(`${businessType}-cancel_reason_now-cancel-dialog`, {}, { isAllowRetry: true });
          const isTritxCancelable =
            tritxRefundStage?.cancelStage && tritxRefundStage.cancelStage !== 'Canceling' && tritxRefundStage.cancelStage !== 'CancelImpossible';

          showDialog({
            title: `${taxName} 환급을 취소할까요?`,
            description: isTritxCancelable ? (
              <div className="flex flex-col gap-2">
                <div>취소하면 진행 중인 환급이 중단돼요.</div>
                <div className="caption-small-regular"> 진행 중인 양도소득세 환급은 자동으로 취소되지 않으며, 별도로 취소 접수를 진행해주셔야 해요.</div>
              </div>
            ) : (
              <div>취소하면 진행 중인 환급이 중단돼요.</div>
            ),
            buttons: {
              primary: {
                text: '환급 취소',
                variant: 'filled-error',
                onClick: () => {
                  requestApplyCancel(
                    () => {
                      sendViewEvent(`${businessType}-cancel_reason_cancel-complete`, {}, { isAllowRetry: true });
                      showDialog({
                        title: `${taxName} 환급이 취소됐어요`,
                        description: (
                          <div>
                            다시 환급 신청을 원하시면, <br />
                            환급 받기를 다시 진행해주세요.
                          </div>
                        ),
                        buttons: { primary: { text: '확인', onClick: goHomePage } }
                      });
                    },
                    () => {
                      toast({ content: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.', icon: 'error', iconColor: 'red.600' });
                    }
                  );
                }
              },
              secondary: { text: '아니요' }
            }
          });
          break;
        }
        case 'Canceling':
        case 'CancelImpossible':
        default: {
          sendViewEvent(`${businessType}-cancel_reason_impossible-cancel`, typeAttribute, { isAllowRetry: false });
          showDialog({
            title: '취소 신청에 실패했어요',
            description: '진행 단계에 변동이 있어 취소 신청이 어려워요. 내 사업체 목록에서 최신화된 환급 단계를 확인해주세요.',
            buttons: { primary: { text: '확인', onClick: goHomePage } }
          });
          break;
        }
      }
    } else {
      const isIncomeCancelable = refundStage?.cancelStage && refundStage.cancelStage !== 'Canceling' && refundStage.cancelStage !== 'CancelImpossible';

      showDialog({
        title: `${taxName} 환급을 취소할까요?`,
        description: isIncomeCancelable ? (
          <div className="flex flex-col gap-2">
            <div>이미 국세청에 신고된 경우 즉시 취소가 어려워요. 취소를 접수하면 담당자 확인을 거쳐 진행돼요.</div>
            <div className="caption-small-regular"> 진행 중인 종합소득세 환급은 자동으로 취소되지 않으며, 별도로 취소 접수를 진행해주셔야 해요.</div>
          </div>
        ) : (
          <div>이미 국세청에 신고된 경우 즉시 취소가 어려워요. 취소를 접수하면 담당자 확인을 거쳐 진행돼요.</div>
        ),
        buttons: {
          primary: {
            text: '환급 취소',
            variant: 'filled-error',
            onClick: () => {
              requestApplyCancel(
                () => {
                  sendViewEvent(`${businessType}-cancel_reason_cancel-complete`, {}, { isAllowRetry: true });
                  showDialog({
                    title: '환급 취소 신청을 접수했어요',
                    description: (
                      <div>
                        환급 진행 상태 확인 후 취소가 완료돼요.
                        <br />
                        필요한 경우 담당자가 연락드릴게요.
                      </div>
                    ),
                    buttons: { primary: { text: '확인', onClick: goHomePage } }
                  });
                },
                () => {
                  toast({ content: '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.', icon: 'error', iconColor: 'red.600' });
                }
              );
            }
          },
          secondary: { text: '아니요' }
        }
      });
    }
  };

  return (
    <div>
      <div className="page-padding">
        <Title.Headline title={`환급 신청을 취소하시는\n이유가 궁금해요`} size="large" subText="불편하신 점이 있다면 개선할게요." />
        <div className="pb-4" />
      </div>

      <CancelReasonList
        reasons={reasons}
        name={name}
        selectedIndex={selectedValue}
        etcContent={etcContent}
        handleChangeSelect={handleChangeSelect}
        handleChangeEtcContent={handleChangeEtcContent}
        businessType={businessType}
      />

      <div className="page-padding mb-10" hidden={selectedValue === null}>
        <Button.FullWidth state="primary" onClick={handleContinue}>
          환급 계속 진행하기
        </Button.FullWidth>
        <div className="pb-3" />
        <Button.FullWidth onClick={handleCancel} disabled={isApplyCancelLoading || isTritxApplyCancelLoading}>
          환급 취소하기
        </Button.FullWidth>
      </div>
    </div>
  );
};
